import { ChevronDown, ChevronRight, X } from 'lucide-react';
import { useState } from 'react';

import { UploadProgress as UploadProgressType } from '../../matter-details/documents/types';
import { UploadProgressItem } from './UploadProgressItem';

interface UploadProgressProps {
  progress: UploadProgressType[];
  error: string | null;
  onDismissError: () => void;
  onClearCompleted: () => void;
}

export const UploadProgress = ({
  progress,
  error,
  onDismissError,
  onClearCompleted,
}: UploadProgressProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!progress.length && !error) return null;

  const completedCount = progress.filter(
    (p) => p.status === 'completed',
  ).length;
  const errorCount = progress.filter((p) => p.status === 'error').length;
  const activeCount = progress.filter((p) => p.status === 'uploading').length;
  const pendingCount = progress.filter((p) => p.status === 'pending').length;

  return (
    <div className="mx-2 mt-1 mb-0 rounded-sm border border-border bg-muted/40 transition-all">
      {/* Header */}
      <button
        className="flex w-full items-center justify-between px-2 py-0.5 text-[11px] cursor-pointer hover:bg-muted text-left"
        onClick={() => setIsCollapsed(!isCollapsed)}
        type="button"
      >
        <div className="flex items-center space-x-1">
          {isCollapsed ? (
            <ChevronRight className="h-2.5 w-2.5" />
          ) : (
            <ChevronDown className="h-2.5 w-2.5" />
          )}
          <span className="font-medium">Uploads</span>
          {activeCount > 0 && (
            <span className="text-[10px] text-muted-foreground">
              {activeCount} in progress
            </span>
          )}
          {errorCount > 0 && (
            <span className="text-[10px] text-destructive">
              {errorCount} failed
            </span>
          )}
          {completedCount > 0 && (
            <span className="text-[10px] text-green-600">
              {completedCount} completed
            </span>
          )}
        </div>

        <div className="flex items-center space-x-1">
          {completedCount > 0 && (
            <span
              role="button"
              tabIndex={0}
              className="text-[10px] text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onClearCompleted();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.stopPropagation();
                  onClearCompleted();
                }
              }}
            >
              Clear completed
            </span>
          )}
          {progress.length > 0 && (
            <X
              className="h-3 w-3 text-muted-foreground hover:text-foreground cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                onClearCompleted();
              }}
            />
          )}
        </div>
      </button>

      {/* Collapsed Content */}
      {!isCollapsed && (
        <div className="px-2 py-0.5 space-y-0.5 max-h-24 overflow-auto">
          {/* Error message */}
          {error && (
            <div className="bg-destructive/10 text-destructive rounded-sm px-2 py-0.5 text-[11px] flex items-center justify-between">
              <span>{error}</span>
              <button
                type="button"
                onClick={onDismissError}
                className="ml-1 flex-shrink-0"
              >
                <X className="h-2.5 w-2.5 cursor-pointer" />
              </button>
            </div>
          )}

          {/* Upload items */}
          {progress.map((item, index) => (
            <UploadProgressItem key={index} progress={item} />
          ))}
        </div>
      )}
    </div>
  );
};

export default UploadProgress;
